import * as Sentry from '@sentry/react';
import type { Router } from '@tanstack/react-router';

const SENTRY_ENVIRONMENT = {
  LOCAL: 'local',
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
} as const;

type SentryEnvironment = (typeof SENTRY_ENVIRONMENT)[keyof typeof SENTRY_ENVIRONMENT];

const sentryEnabledEnvironments: SentryEnvironment[] = [
  SENTRY_ENVIRONMENT.DEVELOPMENT,
  SENTRY_ENVIRONMENT.STAGING,
  SENTRY_ENVIRONMENT.PRODUCTION,
];

const getEnvironment = (env?: string) =>
  Object.values(SENTRY_ENVIRONMENT).find((e) => e === env) || SENTRY_ENVIRONMENT.LOCAL;

const isSentryEnabledForEnvironment = (env: SentryEnvironment) =>
  env ? sentryEnabledEnvironments.includes(env) : false;

const environment = getEnvironment(import.meta.env.VITE_SENTRY_ENVIRONMENT);

export const initSentry = (router: Router<any, any, any>) => {
  if (isSentryEnabledForEnvironment(environment)) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment,
      enabled: true,
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
          response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        },
        httpBodies: [],
        urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        genAI: { inputs: false, outputs: false },
        databaseQueryData: false,
        queues: false,
        graphQL: { document: false, variables: false },
      },

      // Workaround for: https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/40
      ignoreErrors: [
        'Non-Error promise rejection captured',
        'Non-Error exception captured',
        /is not a child of this node/,
      ],

      // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
      tracesSampleRate: 1,

      // Setting this option to true will print useful information to the console while you're setting up Sentry.
      debug: false,

      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  }
};
